/**
 * Shared discount-allocation + deposit-split math.
 *
 * Every surface that prices a deposit order runs through here: the server's
 * single-code and stacked charge paths, the widget & webcomponents basket memos,
 * and the checkout preview hook. One pure module means the "Pay" total the customer
 * sees and the amount Stripe actually charges are computed by the exact same code —
 * the client/server drift that produced DEV-277's overcharge findings can't recur.
 *
 * Callers price codes through `allocateLineDiscounts` (the line model below):
 * an item's add-ons are their own allocation line, so they are discounted like
 * everything else, but an add-on share never moves a deposit row (DEV-327).
 */

export type AllocationItem = {
  id: string;
  /**
   * The per-item base the discount applies to, in pence. For a DEPOSIT item this is
   * the full contract line (deposit-due-now + remaining balance); for a normal item
   * it's `price × quantity`.
   */
  base: number;
};

export type AllocationCode = {
  type: "fixed" | "percentage";
  /** Fixed: face amount in pence. Percentage: whole-number percent (10 = 10%). */
  value: number;
  /**
   * Item ids this code applies to. `undefined` = every item (the caller resolves
   * any per-code eligibility / restrictions before calling).
   */
  eligibleItemIds?: string[];
};

export type AllocationResult = {
  /** Discount attributed to each item id, in pence (0 for untouched items). */
  perItem: Map<string, number>;
  /**
   * Total discount in pence. Equals `Σ perItem` (penny-conserving by construction)
   * and — for the non-pathological cases — the legacy `applyDiscountStack` aggregate.
   */
  total: number;
  /**
   * What each code applied, in pence, in the same order as the `codes` argument
   * (fixed codes are still evaluated before percentage ones). `Σ perCode === total`
   * except when the subtotal clamp bites.
   */
  perCode: number[];
};

/**
 * Allocate one or more discount codes across items, mirroring the established stack
 * math: fixed codes first in entry order, then percentage codes **additive** on the
 * post-fixed remainder (not compounding). Fixed codes consume each item's remaining
 * so later codes price the reduced amount; percentage codes read that remaining but
 * don't consume it. Each code's applied amount is spread across its eligible items
 * proportional to their remaining, with the **last eligible item taking the exact
 * remainder** so pennies are conserved. No item is ever attributed more than its base.
 */
export function allocateDiscounts(
  items: AllocationItem[],
  codes: AllocationCode[]
): AllocationResult {
  const remaining = new Map<string, number>();
  const perItem = new Map<string, number>();
  for (const it of items) {
    remaining.set(it.id, it.base);
    perItem.set(it.id, 0);
  }
  const subtotal = items.reduce((s, it) => s + it.base, 0);

  const eligibleOf = (code: AllocationCode): AllocationItem[] =>
    code.eligibleItemIds == null
      ? items
      : items.filter((it) => code.eligibleItemIds!.includes(it.id));

  // Spread `applied` across `elig` proportional to current remaining; the last item
  // takes the exact remainder so nothing is lost to rounding. Attribute to perItem
  // (never above the item's base). `decrement` consumes remaining for fixed codes;
  // percentage codes leave it so the next % code sees the same post-fixed base.
  // Only lines with something left take a share: a zero line can't absorb any,
  // and a negative one (a net price adjustment, e.g. a cart discount) only
  // lowers what the code applies to. Either, left in, could end up as the
  // "last" line and swallow the remainder, under- or over-attributing.
  const spread = (
    eligAll: AllocationItem[],
    eligibleNet: number,
    applied: number,
    decrement: boolean
  ): number => {
    if (eligibleNet <= 0 || applied <= 0) return 0;
    const elig = eligAll.filter((it) => (remaining.get(it.id) ?? 0) > 0);
    const eligibleRemaining = elig.reduce((s, it) => s + (remaining.get(it.id) ?? 0), 0);
    if (eligibleRemaining <= 0) return 0;
    let allocated = 0;
    let attributed = 0;
    for (let i = 0; i < elig.length; i++) {
      const it = elig[i];
      const itemRemaining = remaining.get(it.id) ?? 0;
      const rawShare =
        i === elig.length - 1
          ? applied - allocated
          : Math.round((itemRemaining / eligibleRemaining) * applied);
      const share = Math.max(0, Math.min(rawShare, itemRemaining));
      allocated += share;
      if (decrement) remaining.set(it.id, itemRemaining - share);
      const prior = perItem.get(it.id) ?? 0;
      const capped = Math.max(0, Math.min(share, it.base - prior));
      perItem.set(it.id, prior + capped);
      attributed += capped;
    }
    return attributed;
  };

  let total = 0;
  const perCode = codes.map(() => 0);

  // 1) Fixed codes, in entry order.
  codes.forEach((code, idx) => {
    if (code.type !== "fixed") return;
    const elig = eligibleOf(code);
    const eligibleRemaining = elig.reduce((s, it) => s + (remaining.get(it.id) ?? 0), 0);
    const applied = Math.min(Math.max(0, code.value || 0), eligibleRemaining);
    const attributed = spread(elig, eligibleRemaining, applied, true);
    perCode[idx] = attributed;
    total += attributed;
  });
  // 2) Percentage codes, additive on the post-fixed remainder (no compounding).
  codes.forEach((code, idx) => {
    if (code.type !== "percentage") return;
    const elig = eligibleOf(code);
    const eligibleRemaining = elig.reduce((s, it) => s + (remaining.get(it.id) ?? 0), 0);
    const applied = Math.min(
      Math.round((eligibleRemaining * Math.max(0, code.value || 0)) / 100),
      eligibleRemaining
    );
    const attributed = spread(elig, eligibleRemaining, applied, false);
    perCode[idx] = attributed;
    total += attributed;
  });

  return { perItem, total: Math.min(total, subtotal), perCode };
}

// ── Line model: add-ons as their own allocation line ────────────────────────
//
// A basket item's discount base is split into two lines: its CORE (a deposit
// item's deposit-lines contract, or a normal item's charge minus add-ons) and
// its ADD-ONS (always charged now, in full). Codes are allocated across lines,
// so a deposit item's add-ons are discounted like everything else (DEV-327)
// while the add-on share NEVER touches the deposit rows — those must stay the
// deposit lines' contract for the balance legs and the request-to-book plan.
// A code with `excludeAddOns` simply leaves the add-on lines out of its
// eligibility. An item without add-ons yields exactly the one line it always
// did, so add-on-free baskets allocate byte-identically.

export type DiscountBaseItem = {
  id: string;
  /** True when the item is priced as a deposit / instalment plan. */
  isDeposit?: boolean;
  /**
   * Deposit item: its deposit lines' contract (deposit-due-now + balance).
   * Other items: charged price × quantity MINUS the add-ons. Never includes
   * add-ons — they are their own line.
   */
  coreBase: number;
  /** Deposit item: its deposit-due-now (the core's now-leg). Ignored otherwise. */
  depositNow?: number;
  /** Add-on total for the item, pence (charged now in full). */
  addOnPence?: number;
};

/**
 * A basket line (checkout snapshot on the server, store item on the clients)
 * as the line model sees it:
 * - deposit item: core = its deposit lines' contract (`depositAmount +
 *   remainingBalance`, already line totals), now-leg = `depositAmount`.
 *   Full-price lines inside a mixed deposit item stay outside the base;
 * - any other item: core = its charge (`price × quantity`) minus the add-ons.
 *
 * `addOnTotalPence` is the server-resolved add-on total per unit (stamped from
 * basket validation). Without it the item has no add-on line and allocates
 * exactly as before. It is clamped to the charge so a stale stamp can never
 * invent a discount base.
 */
export function toDiscountBaseItem(item: {
  id: string;
  /** The charged unit price (the charge-now for a deposit item), pence. */
  price?: number;
  quantity?: number;
  isDeposit?: boolean;
  depositAmount?: number;
  remainingBalance?: number;
  addOnTotalPence?: number;
}): DiscountBaseItem {
  // `?? 1`, not `|| 1`: a quantity-0 line is charged £0 and must add nothing
  // to a discount base (absent quantities default to 1).
  const qty = item.quantity ?? 1;
  const charged = Math.max(0, (item.price ?? 0) * qty);
  const addOnPence = Math.min(Math.max(0, (item.addOnTotalPence ?? 0) * qty), charged);
  if (item.isDeposit) {
    const depositNow = item.depositAmount ?? 0;
    return {
      id: item.id,
      isDeposit: true,
      coreBase: depositNow + (item.remainingBalance ?? 0),
      depositNow,
      addOnPence,
    };
  }
  return { id: item.id, coreBase: charged - addOnPence, addOnPence };
}

const ADD_ON_LINE_SUFFIX = "::addons";

/** Allocation-line id of an item's add-ons. The core line keeps the item id. */
export function addOnLineId(itemId: string): string {
  return `${itemId}${ADD_ON_LINE_SUFFIX}`;
}

/**
 * One core line per item (id = item id) + one add-on line when it has add-ons.
 * A core base is taken as given: a negative one is a net price adjustment
 * (e.g. a preview's cart discount) that lowers what a code applies to without
 * taking a share itself (see allocateDiscounts).
 */
export function buildDiscountLines(items: DiscountBaseItem[]): AllocationItem[] {
  const lines: AllocationItem[] = [];
  for (const it of items) {
    lines.push({ id: it.id, base: it.coreBase });
    if ((it.addOnPence ?? 0) > 0) {
      lines.push({ id: addOnLineId(it.id), base: it.addOnPence! });
    }
  }
  return lines;
}

/**
 * The line ids a code applies to: the core line of every eligible item, plus
 * its add-on line unless the code excludes add-ons. `eligibleItemIds`
 * undefined = every item.
 */
export function eligibleLineIds(
  items: DiscountBaseItem[],
  eligibleItemIds: string[] | undefined,
  excludeAddOns: boolean
): string[] {
  const ids: string[] = [];
  for (const it of items) {
    if (eligibleItemIds && !eligibleItemIds.includes(it.id)) continue;
    ids.push(it.id);
    if (!excludeAddOns && (it.addOnPence ?? 0) > 0) ids.push(addOnLineId(it.id));
  }
  return ids;
}

export type LineDiscountCode = {
  type: "fixed" | "percentage";
  /** Fixed: face amount in pence. Percentage: whole-number percent. */
  value: number;
  /** Item ids this code applies to; `undefined` = every item. */
  eligibleItemIds?: string[];
  /** The code discounts tickets/products only — add-ons are charged in full. */
  excludeAddOns?: boolean;
};

export type LineDiscountResult = {
  /** Per item id: the discount on its core (what a deposit split consumes). */
  core: Map<string, number>;
  /** Per item id: the discount on its add-ons (charged-now only). */
  addOns: Map<string, number>;
  /** Total discount, pence (`Σ core + Σ addOns`). */
  total: number;
  /** What each code applied, in `codes` order. */
  perCode: number[];
  /** Portion of `total` off what is charged now (deposit now-legs + every charged-now line). */
  nowShare: number;
  /** Portion of `total` off deposit balances (collected later). */
  laterShare: number;
};

/**
 * Allocate codes across the line model and fold the result per item and
 * now/later. The single entry point for the server charge paths and every
 * client preview, so "Pay" and the PaymentIntent come from the same code.
 */
export function allocateLineDiscounts(
  items: DiscountBaseItem[],
  codes: LineDiscountCode[]
): LineDiscountResult {
  const { perItem, total, perCode } = allocateDiscounts(
    buildDiscountLines(items),
    codes.map((c) => ({
      type: c.type,
      value: c.value,
      eligibleItemIds: eligibleLineIds(items, c.eligibleItemIds, !!c.excludeAddOns),
    }))
  );
  const core = new Map<string, number>();
  const addOns = new Map<string, number>();
  const shareLines: DepositShareLine[] = [];
  for (const it of items) {
    core.set(it.id, perItem.get(it.id) ?? 0);
    addOns.set(it.id, perItem.get(addOnLineId(it.id)) ?? 0);
    shareLines.push({
      id: it.id,
      isDeposit: !!it.isDeposit,
      depositNow: it.isDeposit ? it.depositNow ?? 0 : it.coreBase,
      contractLine: it.coreBase,
    });
  }
  const { nowShare: coreNow, laterShare } = sumDepositShares(shareLines, core);
  let addOnTotal = 0;
  for (const v of addOns.values()) addOnTotal += v;
  return { core, addOns, total, perCode, nowShare: coreNow + addOnTotal, laterShare };
}

/**
 * Does this discount document exclude add-ons? Promo codes only: a gift card
 * (or credit) is a payment, not a price change, so it always covers add-ons.
 * Absent on pre-feature codes → false (add-ons discounted, the default).
 */
export function codeExcludesAddOns(
  doc:
    | {
        excludeAddOns?: boolean | null;
        nature?: string | null;
        multipleUsageGiftCard?: boolean | null;
      }
    | null
    | undefined
): boolean {
  if (!doc?.excludeAddOns) return false;
  if (doc.nature === "GIFTCARD" || doc.nature === "CREDIT" || doc.multipleUsageGiftCard) {
    return false;
  }
  return true;
}

/**
 * A single code's discount base for a one-line price (staff-made bookings,
 * the legacy direct checkout): the price, less its add-ons when the code
 * excludes them. Never negative. The basket paths use the line model
 * (`allocateLineDiscounts`) instead; this is its one-item equivalent.
 */
export function codeBasePence(
  doc: Parameters<typeof codeExcludesAddOns>[0],
  pricePence: number,
  addOnPence: number
): number {
  const price = Math.max(0, pricePence || 0);
  return codeExcludesAddOns(doc) ? Math.max(0, price - Math.max(0, addOnPence || 0)) : price;
}

/**
 * Apply one Discount document to a one-line price, pence → the price after
 * it: PERCENTAGE rounds the discount UP (the long-standing legacy rule),
 * PRICE takes at most the base, and a code that excludes add-ons is priced on
 * the rest (codeBasePence). Unknown types leave the price alone. Used by the
 * direct (non-basket) charge paths so they apply a code one way.
 */
export function applyCodeToPricePence(
  doc:
    | (Parameters<typeof codeExcludesAddOns>[0] & { type?: string | null; value?: number | null })
    | null
    | undefined,
  pricePence: number,
  addOnPence: number
): number {
  if (!doc) return pricePence;
  const base = codeBasePence(doc, pricePence, addOnPence);
  const value = Math.max(0, doc.value || 0);
  if (doc.type === "PERCENTAGE") return pricePence - Math.ceil((base * Math.min(value, 100)) / 100);
  if (doc.type === "PRICE") return Math.max(0, pricePence - Math.min(value, base));
  return pricePence;
}

/**
 * Split an item's discount across N instalment amounts, proportionally to each
 * amount, penny-conserving: `Σ shares === itemDiscount` exactly (the LAST leg
 * takes the exact remainder). Every share but the last is clamped to
 * `[0, min(amount, still-unallocated)]`, so no leg's charge can go negative and
 * a fixed discount larger than the early legs cascades into the later ones.
 *
 * The 2-leg case is byte-identical to the original `splitDepositDiscount`
 * (which now delegates here) — including its defensive edges: a non-positive
 * discount yields all-zero shares, and a non-positive total drops the whole
 * discount on the final leg.
 */
export function splitAcrossInstalments(args: {
  itemDiscount: number;
  /** Instalment amounts in payment order, pence; `amounts[0]` is charged now. */
  amounts: number[];
}): number[] {
  const { itemDiscount, amounts } = args;
  const n = amounts.length;
  if (n === 0) return [];
  const shares = new Array<number>(n).fill(0);
  if (itemDiscount <= 0) return shares;
  const total = amounts.reduce((s, a) => s + a, 0);
  if (total <= 0) {
    // Defensive (mirrors the historical splitDepositDiscount): nothing sensible
    // to prorate against — the whole discount lands on the final leg.
    shares[n - 1] = itemDiscount;
    return shares;
  }
  let remaining = itemDiscount;
  for (let i = 0; i < n - 1; i++) {
    const raw = Math.round((itemDiscount * amounts[i]) / total);
    const share = Math.max(0, Math.min(raw, amounts[i], remaining));
    shares[i] = share;
    remaining -= share;
  }
  // Exact remainder — penny conservation. Deliberately unclamped: upstream
  // (allocateDiscounts) caps the discount at the contract base, so an overflow
  // here only happens on impossible inputs and must not silently lose pennies.
  shares[n - 1] = remaining;
  return shares;
}

export type DepositSplit = { nowShare: number; laterShare: number };

/**
 * Split an item's discount across the deposit-due-now and the later balance in the
 * deposit : balance ratio, so `nowShare + laterShare === itemDiscount` exactly and
 * the customer pays the discounted contract whether charged now or later. `nowShare`
 * is clamped to `[0, depositNow]` so the deposit charge can never go negative.
 *
 * The N=2 special case of `splitAcrossInstalments` — kept as the ergonomic 2-leg
 * API; the existing call sites and test vectors double as the delegation's
 * byte-parity proof.
 */
export function splitDepositDiscount(args: {
  itemDiscount: number;
  depositNow: number;
  contractLine: number;
}): DepositSplit {
  const { itemDiscount, depositNow, contractLine } = args;
  const [nowShare, laterShare] = splitAcrossInstalments({
    itemDiscount,
    amounts: [depositNow, contractLine - depositNow],
  });
  return { nowShare: nowShare ?? 0, laterShare: laterShare ?? 0 };
}

export type DepositShareLine = {
  id: string;
  /** True for a deposit line — its discount splits now/later. Absent/false = a charged-now line. */
  isDeposit?: boolean;
  /** Amount charged now for this line, in pence (the deposit-due-now for a deposit line). */
  depositNow: number;
  /** Full contract this line's discount applies to, in pence (deposit + balance for a deposit line). */
  contractLine: number;
};

export type DepositShareTotals = { nowShare: number; laterShare: number };

/**
 * Fold a per-item discount allocation into its NOW and LATER portions across a set of
 * lines, using the same deposit:balance split the server charge applies. A deposit line
 * contributes `splitDepositDiscount(...).nowShare` / `.laterShare`; a charged-now line
 * contributes its whole discount to `nowShare` (nothing later). Shared by the widget &
 * webcomponents basket memos (which read `nowShare`) and the checkout preview hook
 * (which reads both) so all three display exactly what the server charges/stamps.
 */
export function sumDepositShares(
  lines: DepositShareLine[],
  perItem: Map<string, number>
): DepositShareTotals {
  let nowShare = 0;
  let laterShare = 0;
  for (const l of lines) {
    const itemDiscount = perItem.get(l.id) ?? 0;
    if (l.isDeposit) {
      const split = splitDepositDiscount({
        itemDiscount,
        depositNow: l.depositNow,
        contractLine: l.contractLine,
      });
      nowShare += split.nowShare;
      laterShare += split.laterShare;
    } else {
      nowShare += itemDiscount;
    }
  }
  return { nowShare, laterShare };
}

export type InstalmentShareLine = {
  id: string;
  /**
   * The line's instalment amounts in payment order (pence); `amounts[0]` is
   * charged now. A pay-in-full line is just a 1-leg plan: `[chargedNow]` — its
   * whole discount lands on `nowShare`, exactly like `sumDepositShares`'
   * non-deposit branch.
   */
  amounts: number[];
};

export type InstalmentShareTotals = {
  /** Discount on what is charged now (leg 0 across all lines + full pay-in-full discounts). */
  nowShare: number;
  /** Discount on everything collected later (legs 1..N across all lines). */
  laterShare: number;
  /**
   * Each line's full share vector (same order/length as its `amounts`) — for
   * rendering a line's discounted schedule. `nowShare`/`laterShare` are its fold.
   */
  perLine: Map<string, number[]>;
};

/**
 * Fold a per-item discount allocation across N-instalment lines — the general
 * form of `sumDepositShares` (which is the uniform 2-leg fold). Totals are
 * byte-compatible with it for 2-leg lines; `perLine` additionally exposes the
 * per-leg detail the instalment schedule display needs, which a single
 * aggregate cannot express when lines carry different row counts.
 */
export function sumInstalmentShares(
  lines: InstalmentShareLine[],
  perItem: Map<string, number>
): InstalmentShareTotals {
  let nowShare = 0;
  let laterShare = 0;
  const perLine = new Map<string, number[]>();
  for (const l of lines) {
    const itemDiscount = perItem.get(l.id) ?? 0;
    const shares = splitAcrossInstalments({ itemDiscount, amounts: l.amounts });
    perLine.set(l.id, shares);
    nowShare += shares[0] ?? 0;
    for (let k = 1; k < shares.length; k++) laterShare += shares[k];
  }
  return { nowShare, laterShare, perLine };
}
